import { useRef, useState } from 'react'
import { Camera, X, Loader2 } from 'lucide-react'

interface CameraCaptureProps {
  onCapture: (base64: string) => void
  onClear: () => void
  disabled?: boolean
  compact?: boolean
}

function compressImage(file: File, maxSize: number, quality: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let { width, height } = img
        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = (height / width) * maxSize
            width = maxSize
          } else {
            width = (width / height) * maxSize
            height = maxSize
          }
        }
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx) { reject(new Error('Canvas not supported')); return }
        ctx.drawImage(img, 0, 0, width, height)
        const dataUrl = canvas.toDataURL('image/jpeg', quality)
        // Strip "data:image/jpeg;base64," prefix
        const base64 = dataUrl.split(',')[1]
        resolve(base64)
      }
      img.onerror = () => reject(new Error('Failed to load image'))
      img.src = e.target?.result as string
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

export default function CameraCapture({ onCapture, onClear, disabled, compact }: CameraCaptureProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  const handleCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsProcessing(true)
    try {
      const base64 = await compressImage(file, 640, 0.7)
      setPreview(`data:image/jpeg;base64,${base64}`)
      onCapture(base64)
    } catch {
      console.error('Failed to process image')
    } finally {
      setIsProcessing(false)
      // Reset input so same file can be re-selected
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const handleClear = () => {
    setPreview(null)
    onClear()
    if (inputRef.current) inputRef.current.value = ''
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="user"
          onChange={handleCapture}
          className="hidden"
          disabled={disabled}
        />
        {preview ? (
          <div className="relative">
            <img src={preview} alt="Selfie" className="w-10 h-10 rounded-lg object-cover" />
            <button
              onClick={handleClear}
              className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center"
              type="button"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => inputRef.current?.click()}
            disabled={disabled || isProcessing}
            type="button"
            className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            {isProcessing ? (
              <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
            ) : (
              <Camera className="h-5 w-5 text-gray-500" />
            )}
          </button>
        )}
      </div>
    )
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="user"
        onChange={handleCapture}
        className="hidden"
        disabled={disabled}
      />
      {preview ? (
        <div className="relative inline-block">
          <img src={preview} alt="Selfie" className="w-24 h-24 rounded-lg object-cover" />
          <button
            onClick={handleClear}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow"
            type="button"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => inputRef.current?.click()}
          disabled={disabled || isProcessing}
          type="button"
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors text-sm text-gray-700"
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Camera className="h-4 w-4" />
              Take Selfie
            </>
          )}
        </button>
      )}
    </div>
  )
}
